import { fx_crearBaseLocal, reqToPromise, txDone } from "../../../../utils/idbUtils";

const DB_NAME = "grpc_compras_animales";
const DB_VERSION = 3;
const STORE_COMPRAS = "compras";
const STORE_SYNC = "sync";

/** Item mínimo que necesita el merge de delta (mismo contrato que
 *  `GrpcLoteItem`/etc.: watermark por finca, no uno global). */
export type GrpcCompraAnimalesItem = {
  id: string;
  eliminado: boolean;
  ultimaActualizacion: bigint;
  fincaId: string;
};

/** IndexedDB **nueva y separada** para las compras de animales que trae
 *  `CompraAnimalesGrpcService` (ver CLAUDE.md del Server, sección migración
 *  a gRPC-Web) — mismo patrón que `GrpcLoteDatabase`: delta real por
 *  `fincaId`. */
class GrpcCompraAnimalesDatabase {
  /** Conexión a la base local con el ayudante común, que al cambiar de
   *  versión conserva los datos (ver `fx_crearBaseLocal`). La versión 3
   *  (2026-09-30) se subió a pedido del usuario para una limpieza única al
   *  estrenar el ayudante: un navegador con una versión anterior vacía la
   *  base una sola vez y la vuelve a llenar desde el servidor. */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      { nombre: STORE_COMPRAS, clave: "id", indices: ["fincaId"] },
      { nombre: STORE_SYNC, clave: "fincaId" },
    ],
    { vaciarAntesDe: 3 },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  async getUltimaSincronizacionAsync(fincaId: string): Promise<bigint> {
    const db = await this.open();
    const store = db.transaction(STORE_SYNC, "readonly").objectStore(STORE_SYNC);
    const row = await reqToPromise<{ fincaId: string; ultimaSincronizacion: string } | undefined>(
      store.get(fincaId),
    );
    return row ? BigInt(row.ultimaSincronizacion) : 0n;
  }

  async putDeltaAsync<T extends GrpcCompraAnimalesItem>(
    fincaId: string,
    items: readonly T[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction([STORE_COMPRAS, STORE_SYNC], "readwrite");
    const store = tx.objectStore(STORE_COMPRAS);
    const syncStore = tx.objectStore(STORE_SYNC);

    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
      } else {
        store.put(item);
      }
    }
    syncStore.put({ fincaId, ultimaSincronizacion: nuevoWatermark.toString() });

    await txDone(tx);
  }

  async getListByFincaIdAsync<T>(fincaId: string): Promise<T[]> {
    const db = await this.open();
    const index = db
      .transaction(STORE_COMPRAS, "readonly")
      .objectStore(STORE_COMPRAS)
      .index("fincaId");
    return reqToPromise<T[]>(index.getAll(fincaId));
  }

  /** Actualiza (o inserta) una sola compra sin tocar el resto. */
  async putOneAsync<T extends GrpcCompraAnimalesItem>(item: T): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_COMPRAS, "readwrite");
    tx.objectStore(STORE_COMPRAS).put(item);
    await txDone(tx);
  }

  /** Borra una compra puntual (baja confirmada por el server). */
  async deleteOneAsync(id: string): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_COMPRAS, "readwrite");
    tx.objectStore(STORE_COMPRAS).delete(id);
    await txDone(tx);
  }

  /** Actualiza (o inserta) varias compras en una sola transacción, sin tocar
   *  el resto ni el watermark; las marcadas `eliminado` se borran. Lo usa
   *  `fx_aplicarCambiosMovimientoAsync` con las compras que el server
   *  recalculó por un movimiento (2026-10-08). */
  async putManyAsync<T extends GrpcCompraAnimalesItem>(items: readonly T[]): Promise<void> {
    if (items.length === 0) return;
    const db = await this.open();
    const tx = db.transaction(STORE_COMPRAS, "readwrite");
    const store = tx.objectStore(STORE_COMPRAS);
    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
      } else {
        store.put(item);
      }
    }
    await txDone(tx);
  }
}

/** Singleton de la base de compras de animales por gRPC-Web (con delta por
 *  finca). */
export const grpcCompraAnimalesDatabase = new GrpcCompraAnimalesDatabase();
